import { APIError, UnathorizedError, NetworkError } from './constants'
import { getJWT } from 'services/user'
import { Capacitor } from '@capacitor/core'
import Http from '../../HttpPlugin'

const commonHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
}

const makeCommonHeaders = (extraHeaders = {}) => {
  const jwt = getJWT()
  if (jwt) {
    return {
      ...commonHeaders,
      ...extraHeaders,
      Authorization: jwt,
    }
  }
  return {
    ...commonHeaders,
    ...extraHeaders,
  }
}

const makeFormDataHeaders = (extraHeaders = {}) => {
  const jwt = getJWT()
  if (jwt) {
    return {
      ...extraHeaders,
      Authorization: jwt,
    }
  }
  return {
    ...extraHeaders,
  }
}

/**
 * Intenta hacer la petición con HTTPS, si falla por certificado SSL,
 * intenta con HTTP como fallback (solo en Android)
 */
function tryRequestWithHttpFallback(
  url: string,
  method: string,
  headers: Record<string, string>,
  body?: string,
): Promise<any> {
  // En Android nativo, intentar primero con HTTPS (ignorando certificados)
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
    return Http.requestPromise({
      url,
      method,
      headers,
      body,
      bodyType: 'json',
    })
      .then((response) => {
        if (response.status === 401) {
          throw new UnathorizedError('Forbidden')
        }
        if (response.status < 200 || response.status >= 300) {
          throw new APIError(response.status, response.statusText)
        }

        // Convert JSObject to plain JavaScript object/array
        // JSObject from Capacitor needs to be serialized and parsed to become a plain JS object
        let data = response.data

        // If data is not already an array or plain object, try to convert it
        if (data != null) {
          // Check if it's a JSObject wrapper (has keys method or is not a plain object)
          const needsConversion =
            !Array.isArray(data) &&
            typeof data === 'object' &&
            (data.constructor?.name !== 'Object' ||
              ('keys' in data && typeof data.keys === 'function'))

          if (needsConversion) {
            try {
              // Serialize and parse to convert JSObject to plain JS object/array
              data = JSON.parse(JSON.stringify(data))
            } catch (e) {
              // If conversion fails, try to extract data directly
              if (typeof data === 'object' && 'valueOf' in data) {
                data = data.valueOf()
              }
            }
          }
        }

        return data
      })
      .catch((err) => {
        // No intentar fallback HTTP si es un error de timeout
        if (
          err.message?.includes('timeout') ||
          err.message?.includes('Timeout')
        ) {
          throw new NetworkError(
            err.message ||
              'Request timeout: The server took too long to respond',
          )
        }

        // Si falla por certificado SSL y la URL es HTTPS, intentar con HTTP
        if (
          url.startsWith('https://') &&
          (err.message?.includes('certificate') ||
            err.message?.includes('SSL') ||
            err.message?.includes('cert'))
        ) {
          const httpUrl = url.replace('https://', 'http://')
          console.warn(
            `HTTPS falló por certificado SSL, intentando con HTTP: ${httpUrl}`,
          )
          return Http.requestPromise({
            url: httpUrl,
            method,
            headers,
            body,
            bodyType: 'json',
          })
            .then((response) => {
              if (response.status === 401) {
                throw new UnathorizedError('Forbidden')
              }
              if (response.status < 200 || response.status >= 300) {
                throw new APIError(response.status, response.statusText)
              }
              return response.data
            })
            .catch((httpErr) => {
              throw new NetworkError(
                `HTTP fallback también falló: ${
                  httpErr.message || 'Network error'
                }`,
              )
            })
        }
        throw new NetworkError(err.message || 'Network error')
      })
  }

  // En web/PWA, usar fetch normal (no hay fallback a HTTP por seguridad)
  return fetch(url, {
    method,
    headers,
    body,
  }).then(responseHandler, fetchErrorHandler)
}

export function get(url: string, headers = {}) {
  // En Android nativo, usar el plugin HTTP que ignora certificados SSL caducados
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
    return tryRequestWithHttpFallback(url, 'GET', makeCommonHeaders(headers))
  }

  // En web/PWA, usar fetch normal
  return fetch(url, {
    method: 'GET',
    headers: makeCommonHeaders(headers),
  }).then(responseHandler, fetchErrorHandler)
}

export function post(url: string, data?: any, headers = {}) {
  // En Android nativo, usar el plugin HTTP que ignora certificados SSL caducados
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
    return tryRequestWithHttpFallback(
      url,
      'POST',
      makeCommonHeaders(headers),
      data != null ? JSON.stringify(data) : undefined,
    )
  }

  // En web/PWA, usar fetch normal
  return fetch(url, {
    method: 'POST',
    headers: makeCommonHeaders(headers),
    body: data != null ? JSON.stringify(data) : undefined,
  }).then(responseHandler, fetchErrorHandler)
}

/**
 * Convierte FormData a un objeto que puede ser serializado para el plugin HTTP
 */
async function formDataToObject(formData: FormData): Promise<{
  fields: Record<string, string>
  file?: { name: string; type: string; data: string }
}> {
  const fields: Record<string, string> = {}
  let file: { name: string; type: string; data: string } | undefined

  for (const [key, value] of formData.entries()) {
    if (value instanceof File) {
      // Leer el archivo como base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          const result = reader.result as string
          // Remover el prefijo data:image/...;base64,
          const base64Data = result.includes(',')
            ? result.split(',')[1]
            : result
          resolve(base64Data)
        }
        reader.onerror = reject
        reader.readAsDataURL(value)
      })

      file = {
        name: value.name,
        type: value.type,
        data: base64,
      }
    } else {
      fields[key] = String(value)
    }
  }

  return { fields, file }
}

export function postFormData(url: string, data: FormData, headers = {}) {
  // En Android nativo, convertir FormData y usar el plugin HTTP
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
    return formDataToObject(data)
      .then(async (formDataObj) => {
        // Construir el body como JSON con los datos del formulario
        const body = JSON.stringify({
          ...formDataObj.fields,
          ...(formDataObj.file && {
            file: formDataObj.file,
          }),
        })

        // Intentar primero con HTTPS
        try {
          const response = await Http.requestPromise({
            url,
            method: 'POST',
            headers: makeFormDataHeaders(headers),
            body,
            bodyType: 'multipart',
          })

          if (response.status === 401) {
            throw new UnathorizedError('Forbidden')
          }
          if (response.status < 200 || response.status >= 300) {
            throw new APIError(response.status, response.statusText)
          }
          return response.data
        } catch (err: any) {
          // Si falla por certificado SSL, intentar con HTTP
          if (
            url.startsWith('https://') &&
            (err.message?.includes('certificate') ||
              err.message?.includes('SSL') ||
              err.message?.includes('cert') ||
              err.message?.includes('timeout'))
          ) {
            const httpUrl = url.replace('https://', 'http://')
            console.warn(
              `HTTPS falló por certificado SSL en upload, intentando con HTTP: ${httpUrl}`,
            )
            const httpResponse = await Http.requestPromise({
              url: httpUrl,
              method: 'POST',
              headers: makeFormDataHeaders(headers),
              body,
              bodyType: 'multipart',
            })

            if (httpResponse.status === 401) {
              throw new UnathorizedError('Forbidden')
            }
            if (httpResponse.status < 200 || httpResponse.status >= 300) {
              throw new APIError(httpResponse.status, httpResponse.statusText)
            }
            return httpResponse.data
          }
          throw new NetworkError(err.message || 'Network error')
        }
      })
      .catch((err) => {
        throw new NetworkError(err.message || 'Error processing FormData')
      })
  }

  // En web/PWA, usar fetch normal
  return fetch(url, {
    method: 'POST',
    headers: makeFormDataHeaders(headers),
    body: data,
  }).then(responseHandler, fetchErrorHandler)
}

export function fetchRemoteFile(url: string, signal?: AbortSignal) {
  return fetch(url, {
    method: 'GET',
    headers: makeFormDataHeaders(),
    signal,
  }).then((resp) => {
    if (resp.ok) return resp.blob()
  }, fetchErrorHandler)
  //.catch(fetchErrorHandler)
}

const responseHandler = (response: Response) => {
  if (response.status === 401) {
    throw new UnathorizedError('Forbidden')
  }
  if (!response.ok) {
    throw new APIError(response.status, response.statusText)
  }
  return response.json()
}

const fetchErrorHandler = (err: Error) => {
  throw new NetworkError(err.message)
}
